import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { date: today(), client_id: "", title: "", amount: 0, notes: "" };

export default function Shoots() {
  const { user } = useAuth();
  const [shoots, setShoots] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const canEdit = ["admin", "editor", "smm"].includes(user?.role);
  const canDelete = ["admin", "smm"].includes(user?.role);

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/shoots"), api.get("/clients")]);
    setShoots(s.data);
    setClients(c.data);
  };

  useEffect(() => { load(); }, []);

  const findClient = (id) => clients.find((c) => c.client_id === id);

  const submit = async () => {
    if (!form.date) {
      toast.error("Date required");
      return;
    }
    try {
      const payload = { ...form, client_id: form.client_id || null };
      const r = await api.post("/shoots", payload);
      setShoots((p) => [r.data, ...p]);
      setOpen(false);
      setForm(EMPTY);
      toast.success("Shoot logged");
    } catch (e) {
      toast.error("Failed to log shoot");
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm("Delete this shoot log?")) return;
    try {
      await api.delete(`/shoots/${s.shoot_id}`);
      setShoots((p) => p.filter((x) => x.shoot_id !== s.shoot_id));
      toast.success("Shoot deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const totalRevenue = shoots.reduce((sum, s) => sum + (s.amount || 0), 0);

  return (
    <div data-testid="shoots-page">
      <PageHeader
        eyebrow="Production Log"
        title="Shoots"
        description="Every shoot, its date, client, and amount earned. The single source of truth."
        actions={
          canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-shoot-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.15em] text-xs">New Shoot</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-neutral-950 border border-neutral-800 rounded-none max-w-xl">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">New Shoot</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="shoot-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Amount</Label>
                    <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="shoot-amount-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Title</Label>
                    <Input value={form.title} placeholder="e.g. Brand Promo Day 1" onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Client (optional)</Label>
                    <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="shoot-client-select">
                        <SelectValue placeholder="Link to client" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">— Global (no client) —</SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button onClick={submit} data-testid="shoot-submit-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">Log Shoot</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="px-6 lg:px-10 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900">
          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Total Shoots</div>
            <div className="font-heading text-3xl font-semibold">{shoots.length}</div>
          </div>
          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Revenue</div>
            <div className="font-heading text-3xl font-semibold">₹ {totalRevenue.toLocaleString()}</div>
          </div>
          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Avg / Shoot</div>
            <div className="font-heading text-3xl font-semibold">₹ {shoots.length ? Math.round(totalRevenue / shoots.length).toLocaleString() : 0}</div>
          </div>
        </div>

        <div className="border border-neutral-900 overflow-x-auto">
          <table className="w-full" data-testid="shoots-table">
            <thead>
              <tr className="border-b border-neutral-900 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Title</th>
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-right px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {shoots.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-sm text-neutral-500">No shoots logged.</td></tr>
              )}
              {shoots.map((s) => (
                <tr key={s.shoot_id} className="border-b border-neutral-900 hover:bg-neutral-900/40" data-testid={`shoot-row-${s.shoot_id}`}>
                  <td className="px-4 py-4 text-xs font-mono text-neutral-300">{s.date}</td>
                  <td className="px-4 py-4 text-sm">{s.title || "—"}{s.notes && <div className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{s.notes}</div>}</td>
                  <td className="px-4 py-4 text-sm text-neutral-300">{findClient(s.client_id)?.name || <span className="text-neutral-600">Global</span>}</td>
                  <td className="px-4 py-4 text-right text-sm font-mono">₹ {Number(s.amount).toLocaleString()}</td>
                  <td className="px-4 py-4 text-right">
                    {canDelete && (
                      <button onClick={() => handleDelete(s)} className="text-neutral-500 hover:text-red-400 transition-colors" data-testid={`delete-shoot-${s.shoot_id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
